import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useNotification } from '../contexts/NotificationContext'
import { documentService } from '../services/documentService'
import type { DocumentType, CreateDocumentData, BusinessDocumentTemplate, DocumentParty } from '../types/document'
import {
  DocumentTextIcon,
  ArrowLeftIcon,
  PlusIcon,
  MinusIcon,
  CheckIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  ShieldCheckIcon,
  CameraIcon,
  MapPinIcon,
  BriefcaseIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline'
import AppHeader from '../components/AppHeader'
import ProfileDropdown from '../components/ProfileDropdown'
import NotificationDropdown from '../components/NotificationDropdown'
import SettingsDropdown from '../components/SettingsDropdown'

interface PartyForm extends Omit<DocumentParty, 'id' | 'signed' | 'signedAt'> {
  tempId: string
}

const CreateDocumentPage = () => {
  const { user } = useAuth()
  const { showSuccess, showError } = useNotification()
  const navigate = useNavigate()

  // Form state
  const [step, setStep] = useState(1) // 1: Template Selection, 2: Details, 3: Variables, 4: Parties, 5: Review
  const [selectedTemplate, setSelectedTemplate] = useState<BusinessDocumentTemplate | null>(null)
  const [templates, setTemplates] = useState<BusinessDocumentTemplate[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [planLimits, setPlanLimits] = useState<import('../types/document').DocumentPlanLimits | null>(null)

  // Document form data
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    tags: [] as string[],
    effectiveDate: '',
    expirationDate: '',
    contractValue: '',
    paymentTerms: '',
    cancellationTerms: '',
    governingLaw: '',
    jurisdiction: '',
    signatureRequired: true
  })

  // Template variables
  const [variables, setVariables] = useState<Record<string, string>>({})
  
  // Parties
  const [parties, setParties] = useState<PartyForm[]>([])
  
  // Current tag input
  const [currentTag, setCurrentTag] = useState('')

  useEffect(() => {
    loadTemplates()
    loadPlanLimits()
  }, [user])

  const loadTemplates = async () => {
    const result = await documentService.getDocumentTemplates()
    if (result.success && result.templates) {
      setTemplates(result.templates)
    }
  }

  const loadPlanLimits = async () => {
    if (!user) return
    
    const limits = await documentService.getPlanLimits(user.subscriptionStatus || 'freemium', user.id)
    setPlanLimits(limits)
  }

  const getDocumentTypeIcon = (type: DocumentType) => {
    switch (type) {
      case 'subcontractor_agreement':
      case 'independent_contractor':
        return <BriefcaseIcon className="w-6 h-6" />
      case 'nda':
        return <ShieldCheckIcon className="w-6 h-6" />
      case 'client_contract':
      case 'service_agreement':
        return <DocumentTextIcon className="w-6 h-6" />
      case 'model_release':
        return <CameraIcon className="w-6 h-6" />
      case 'location_release':
        return <MapPinIcon className="w-6 h-6" />
      case 'vendor_agreement':
        return <BuildingOfficeIcon className="w-6 h-6" />
      case 'employment_contract':
        return <UserGroupIcon className="w-6 h-6" />
      default:
        return <DocumentDuplicateIcon className="w-6 h-6" />
    }
  }

  const selectTemplate = (template: BusinessDocumentTemplate) => {
    setSelectedTemplate(template)
    setFormData(prev => ({
      ...prev,
      category: template.category,
      title: template.name
    }))
    
    // Initialize variables with empty values
    const initialVariables: Record<string, string> = {}
    template.variables.forEach(variable => {
      initialVariables[variable.key] = variable.defaultValue || ''
    })
    setVariables(initialVariables)
    
    setStep(2)
  }

  const addTag = () => {
    if (currentTag.trim() && !formData.tags.includes(currentTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, currentTag.trim()]
      }))
      setCurrentTag('')
    }
  }

  const removeTag = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }))
  }

  const addParty = () => {
    const newParty: PartyForm = {
      tempId: Date.now().toString(),
      type: 'client',
      name: '',
      email: '',
      company: '',
      role: '',
      address: '',
      phone: '',
      signatureRequired: true
    }
    setParties([...parties, newParty])
  }

  const updateParty = (tempId: string, field: keyof PartyForm, value: string | boolean) => {
    setParties(parties.map(party => 
      party.tempId === tempId ? { ...party, [field]: value } : party
    ))
  }

  const removeParty = (tempId: string) => {
    setParties(parties.filter(party => party.tempId !== tempId))
  }

  const handleSubmit = async () => {
    if (!user || !selectedTemplate) return

    // Check plan limits
    if (planLimits && !planLimits.hasUnlimitedDocuments && planLimits.currentMonthUsage >= planLimits.maxDocumentsPerMonth) {
      showError('Limit Reached', 'You have reached your monthly document limit. Please upgrade to create more documents.')
      return
    }

    setIsLoading(true)
    
    try {
      const createData: CreateDocumentData = {
        title: formData.title,
        documentType: selectedTemplate.documentType,
        templateId: selectedTemplate.id,
        description: formData.description,
        tags: formData.tags,
        category: formData.category,
        effectiveDate: formData.effectiveDate || undefined,
        expirationDate: formData.expirationDate || undefined,
        contractValue: formData.contractValue ? parseFloat(formData.contractValue) : undefined,
        paymentTerms: formData.paymentTerms || undefined,
        cancellationTerms: formData.cancellationTerms || undefined,
        governingLaw: formData.governingLaw || undefined,
        jurisdiction: formData.jurisdiction || undefined,
        parties: parties.map(({ tempId, ...party }) => party),
        variables,
        signatureRequired: formData.signatureRequired
      }

      const result = await documentService.createDocument(user.id, createData)
      
      if (result.success) {
        showSuccess('Success', 'Document created successfully!')
        navigate('/documents')
      } else {
        showError('Error', result.error || 'Failed to create document')
      }
    } catch (error) {
      showError('Error', 'Failed to create document')
    } finally {
      setIsLoading(false)
    }
  }

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Choose a Document Template</h2>
              <p className="text-gray-400">Select from our professional business document templates</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {templates.map((template) => (
                <motion.div
                  key={template.id}
                  whileHover={{ scale: 1.02 }}
                  className="glass-mockup rounded-xl p-6 cursor-pointer hover:bg-white/10 transition-all"
                  onClick={() => selectTemplate(template)}
                >
                  <div className="flex items-start space-x-4">
                    <div className="p-3 bg-blue-500/20 rounded-lg">
                      {getDocumentTypeIcon(template.documentType)}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-2">{template.name}</h3>
                      <p className="text-gray-400 text-sm mb-3">{template.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="px-2 py-1 bg-purple-500/20 text-purple-300 text-xs rounded-full">
                          {template.category}
                        </span>
                        <span className="text-blue-400 text-sm">Select →</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )

      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Document Details</h2>
              <p className="text-gray-400">Configure the basic information for your document</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Document Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/5 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none"
                  placeholder="Enter document title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/5 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none"
                  placeholder="Document category"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-3 bg-white/5 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none"
                  placeholder="Brief description of the document"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Effective Date</label>
                <input
                  type="date"
                  value={formData.effectiveDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, effectiveDate: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/5 border border-gray-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Expiration Date</label>
                <input
                  type="date"
                  value={formData.expirationDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, expirationDate: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/5 border border-gray-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Contract Value ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.contractValue}
                  onChange={(e) => setFormData(prev => ({ ...prev, contractValue: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/5 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Governing Law</label>
                <input
                  type="text"
                  value={formData.governingLaw}
                  onChange={(e) => setFormData(prev => ({ ...prev, governingLaw: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/5 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none"
                  placeholder="e.g., State of California"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">Tags</label>
                <div className="flex items-center space-x-2 mb-2">
                  <input
                    type="text"
                    value={currentTag}
                    onChange={(e) => setCurrentTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addTag()}
                    className="flex-1 px-4 py-2 bg-white/5 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none"
                    placeholder="Add a tag"
                  />
                  <button
                    onClick={addTag}
                    className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-blue-500/20 text-blue-300 text-sm rounded-full flex items-center space-x-1"
                    >
                      <span>{tag}</span>
                      <button
                        onClick={() => removeTag(tag)}
                        className="ml-1 text-blue-300 hover:text-white"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )

      case 3:
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Template Variables</h2>
              <p className="text-gray-400">Fill in the template-specific information</p>
            </div>

            {selectedTemplate && selectedTemplate.variables.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {selectedTemplate.variables.map((variable) => (
                  <div key={variable.key}>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {variable.label}
                      {variable.required && <span className="text-red-400 ml-1">*</span>}
                    </label>
                    {variable.type === 'date' ? (
                      <input
                        type="date"
                        value={variables[variable.key] || ''}
                        onChange={(e) => setVariables(prev => ({ ...prev, [variable.key]: e.target.value }))}
                        className="w-full px-4 py-3 bg-white/5 border border-gray-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                        required={variable.required}
                      />
                    ) : variable.type === 'currency' ? (
                      <input
                        type="number"
                        step="0.01"
                        value={variables[variable.key] || ''}
                        onChange={(e) => setVariables(prev => ({ ...prev, [variable.key]: e.target.value }))}
                        className="w-full px-4 py-3 bg-white/5 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none"
                        placeholder={variable.placeholder || '0.00'}
                        required={variable.required}
                      />
                    ) : (
                      <input
                        type="text"
                        value={variables[variable.key] || ''}
                        onChange={(e) => setVariables(prev => ({ ...prev, [variable.key]: e.target.value }))}
                        className="w-full px-4 py-3 bg-white/5 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none"
                        placeholder={variable.placeholder || variable.label}
                        required={variable.required}
                      />
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <DocumentTextIcon className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                <p className="text-gray-400">This template doesn't require any additional variables.</p>
              </div>
            )}
          </motion.div>
        )

      case 4:
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Document Parties</h2>
              <p className="text-gray-400">Add the parties involved in this document</p>
            </div>

            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-white">Parties ({parties.length})</h3>
              <button
                onClick={addParty}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                <span>Add Party</span>
              </button>
            </div>

            <div className="space-y-4">
              {parties.map((party) => (
                <div key={party.tempId} className="glass-mockup rounded-xl p-6">
                  <div className="flex justify-between items-start mb-4">
                    <h4 className="text-lg font-medium text-white">Party {parties.indexOf(party) + 1}</h4>
                    <button
                      onClick={() => removeParty(party.tempId)}
                      className="text-red-400 hover:text-red-300 p-1"
                    >
                      <MinusIcon className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Type</label>
                      <select
                        value={party.type}
                        onChange={(e) => updateParty(party.tempId, 'type', e.target.value as any)}
                        className="w-full px-4 py-3 bg-white/5 border border-gray-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                      >
                        <option value="client">Client</option>
                        <option value="subcontractor">Subcontractor</option>
                        <option value="vendor">Vendor</option>
                        <option value="employee">Employee</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Name *</label>
                      <input
                        type="text"
                        value={party.name}
                        onChange={(e) => updateParty(party.tempId, 'name', e.target.value)}
                        className="w-full px-4 py-3 bg-white/5 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none"
                        placeholder="Full name"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Email *</label>
                      <input
                        type="email"
                        value={party.email}
                        onChange={(e) => updateParty(party.tempId, 'email', e.target.value)}
                        className="w-full px-4 py-3 bg-white/5 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none"
                        placeholder="email@example.com"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Role *</label>
                      <input
                        type="text"
                        value={party.role}
                        onChange={(e) => updateParty(party.tempId, 'role', e.target.value)}
                        className="w-full px-4 py-3 bg-white/5 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none"
                        placeholder="e.g., Photographer, Client, Contractor"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Company</label>
                      <input
                        type="text"
                        value={party.company}
                        onChange={(e) => updateParty(party.tempId, 'company', e.target.value)}
                        className="w-full px-4 py-3 bg-white/5 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none"
                        placeholder="Company name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Phone</label>
                      <input
                        type="tel"
                        value={party.phone}
                        onChange={(e) => updateParty(party.tempId, 'phone', e.target.value)}
                        className="w-full px-4 py-3 bg-white/5 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none"
                        placeholder="(555) 123-4567"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={party.signatureRequired}
                          onChange={(e) => updateParty(party.tempId, 'signatureRequired', e.target.checked)}
                          className="rounded border-gray-600 bg-white/5 text-blue-500 focus:ring-blue-500"
                        />
                        <span className="text-gray-300">Signature required from this party</span>
                      </label>
                    </div>
                  </div>
                </div>
              ))}

              {parties.length === 0 && (
                <div className="text-center py-8">
                  <UserGroupIcon className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-400 mb-4">No parties added yet.</p>
                  <button
                    onClick={addParty}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg flex items-center space-x-2 mx-auto transition-colors"
                  >
                    <PlusIcon className="w-5 h-5" />
                    <span>Add First Party</span>
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )

      case 5:
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Review & Create</h2>
              <p className="text-gray-400">Review your document details before creating</p>
            </div>

            <div className="glass-mockup rounded-xl p-6 space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Document Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-400">Title:</span>
                    <span className="text-white ml-2">{formData.title}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Template:</span>
                    <span className="text-white ml-2">{selectedTemplate?.name}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Category:</span>
                    <span className="text-white ml-2">{formData.category}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Parties:</span>
                    <span className="text-white ml-2">{parties.length}</span>
                  </div>
                </div>
              </div>

              {formData.tags.length > 0 && (
                <div>
                  <h4 className="text-md font-medium text-white mb-2">Tags</h4>
                  <div className="flex flex-wrap gap-2">
                    {formData.tags.map((tag, index) => (
                      <span key={index} className="px-2 py-1 bg-blue-500/20 text-blue-300 text-xs rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {parties.length > 0 && (
                <div>
                  <h4 className="text-md font-medium text-white mb-2">Parties</h4>
                  <div className="space-y-2">
                    {parties.map((party, index) => (
                      <div key={party.tempId} className="flex items-center space-x-3 text-sm">
                        <span className="text-gray-400">{index + 1}.</span>
                        <span className="text-white">{party.name}</span>
                        <span className="text-gray-400">({party.role})</span>
                        <span className="text-blue-400">{party.email}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )
    }
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Floating orbs for glassmorphism background effect */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="floating-orb floating-orb-3"></div>
        <div className="floating-orb floating-orb-4"></div>
        <div className="floating-orb floating-orb-5"></div>
      </div>

      {/* Header */}
      <AppHeader>
        <NotificationDropdown />
        <SettingsDropdown />
        <ProfileDropdown />
      </AppHeader>

      {/* Main Content */}
      <main className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Link
              to="/documents"
              className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5 text-white" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-white">Create New Document</h1>
              <p className="text-gray-400">Step {step} of 5</p>
            </div>
          </div>
          {selectedTemplate && (
            <div className="flex items-center space-x-2 text-sm text-gray-400">
              <span>Template:</span>
              <span className="text-white">{selectedTemplate.name}</span>
            </div>
          )}
        </div>

        {/* Plan Limits Bubble */}
        {planLimits && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-8 p-3 rounded-xl border ${
              planLimits.hasUnlimitedDocuments 
                ? 'bg-emerald-500/10 border-emerald-500/30' 
                : planLimits.currentMonthUsage >= planLimits.maxDocumentsPerMonth * 0.8
                  ? 'bg-orange-500/10 border-orange-500/30'
                  : 'bg-blue-500/10 border-blue-500/30'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-lg ${
                  planLimits.hasUnlimitedDocuments 
                    ? 'bg-emerald-500/20' 
                    : planLimits.currentMonthUsage >= planLimits.maxDocumentsPerMonth * 0.8
                      ? 'bg-orange-500/20'
                      : 'bg-blue-500/20'
                }`}>
                  <DocumentTextIcon className={`w-4 h-4 ${
                    planLimits.hasUnlimitedDocuments 
                      ? 'text-emerald-400' 
                      : planLimits.currentMonthUsage >= planLimits.maxDocumentsPerMonth * 0.8
                        ? 'text-orange-400'
                        : 'text-blue-400'
                  }`} />
                </div>
                <div>
                  <p className={`font-medium ${
                    planLimits.hasUnlimitedDocuments 
                      ? 'text-emerald-300' 
                      : planLimits.currentMonthUsage >= planLimits.maxDocumentsPerMonth * 0.8
                        ? 'text-orange-300'
                        : 'text-blue-300'
                  }`}>
                    {planLimits.hasUnlimitedDocuments 
                      ? 'Professional Plan - Unlimited Documents' 
                      : `${planLimits.currentMonthUsage}/${planLimits.maxDocumentsPerMonth} documents this month`
                    }
                  </p>
                  <p className="text-gray-400 text-sm">
                    {planLimits.hasUnlimitedDocuments 
                      ? 'Create unlimited business documents and contracts'
                      : `${planLimits.maxDocumentsPerMonth - planLimits.currentMonthUsage} documents remaining`
                    }
                  </p>
                </div>
              </div>
              {!planLimits.hasUnlimitedDocuments && (
                <Link
                  to="/upgrade"
                  className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                >
                  Upgrade
                </Link>
              )}
            </div>
          </motion.div>
        )}

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center space-x-2 mb-2">
            {[1, 2, 3, 4, 5].map((stepNumber) => (
              <div
                key={stepNumber}
                className={`flex-1 h-2 rounded-full transition-colors ${
                  stepNumber <= step ? 'bg-blue-500' : 'bg-gray-600'
                }`}
              />
            ))}
          </div>
          <div className="flex justify-between text-xs text-gray-400">
            <span>Template</span>
            <span>Details</span>
            <span>Variables</span>
            <span>Parties</span>
            <span>Review</span>
          </div>
        </div>

        {/* Step Content */}
        <div className="glass-mockup rounded-xl p-8">
          {renderStepContent()}
        </div>

        {/* Navigation */}
        <div className="flex justify-between mt-8">
          <button
            onClick={() => step > 1 ? setStep(step - 1) : navigate('/documents')}
            className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
          >
            {step === 1 ? 'Cancel' : 'Previous'}
          </button>

          <button
            onClick={() => {
              if (step < 5) {
                setStep(step + 1)
              } else {
                handleSubmit()
              }
            }}
            disabled={isLoading || (step === 1 && !selectedTemplate)}
            className="px-6 py-3 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center space-x-2"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Creating...</span>
              </>
            ) : step === 5 ? (
              <>
                <CheckIcon className="w-4 h-4" />
                <span>Create Document</span>
              </>
            ) : (
              <span>Next</span>
            )}
          </button>
        </div>
      </main>
    </div>
  )
}

export default CreateDocumentPage 